import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight, type LucideIcon } from 'lucide-react';
import { Card } from './Card';
import { cn } from '../lib/cn';

// Children render OUTSIDE the header Card so sections that carry their own
// Cards (e.g. OrgPublicPageEditor) don't end up double-nested.

export function CollapsibleSection({
  title,
  hint,
  icon: Icon,
  defaultOpen = false,
  right,
  className,
  openSignal,
  children,
}: {
  title: string;
  hint?: string;
  icon?: LucideIcon;
  defaultOpen?: boolean;
  // Optional trailing slot (e.g. a status chip + counts) rendered before the
  // chevron — added for MatchScheduleBoard's per-stage headers; every
  // existing caller omits it and renders byte-for-byte the same as before.
  right?: ReactNode;
  // Optional passthrough onto the section's own root wrapper — added so a
  // caller can give a specific section extra air above (e.g. `!mt-8`) without
  // every other CollapsibleSection call site changing; omitted everywhere
  // else, so this is purely additive.
  className?: string;
  // Bump this (any changing number) to force the section open from OUTSIDE —
  // added for the Phase 4a go-live checklist's "jump to this section" (e.g.
  // Discord binding lives inside a collapsed "Founder Settings" section;
  // scrolling to it does nothing useful if it never opens). Guarded to skip
  // the initial mount — only a real CHANGE forces it open, never the prop
  // merely being present. Omitted everywhere else — purely additive.
  openSignal?: number;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const openSignalRef = useRef(openSignal);
  useEffect(() => {
    if (openSignal !== undefined && openSignal !== openSignalRef.current) {
      openSignalRef.current = openSignal;
      setOpen(true);
    }
  }, [openSignal]);

  return (
    <div className={cn(className)}>
      <Card className="!p-4">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex w-full items-center justify-between gap-3"
        >
          <div className="flex min-w-0 items-center gap-3 text-left">
            {Icon && <Icon className="h-4 w-4 shrink-0 text-ink-meta" />}
            <div className="min-w-0">
              <p className="font-display text-[11px] font-black uppercase tracking-[0.22em] text-forge-300">
                {title}
              </p>
              {hint && <p className="mt-0.5 text-xs text-ink-meta">{hint}</p>}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {right}
            {open
              ? <ChevronDown className="h-4 w-4 shrink-0 text-ink-meta" />
              : <ChevronRight className="h-4 w-4 shrink-0 text-ink-meta" />}
          </div>
        </button>
      </Card>
      {open && <div className="mt-4">{children}</div>}
    </div>
  );
}
